<template>
  <div :class="{ 'custom-select': true, open: open, 'invert-to-top': calculatePosition() }" ref="dropdownMenu" :tabindex="tabindex" @focusout="open = false" @click.stop @keydown="handleKeyDown($event)">
    <div ref="dropdownSelection" :class="{ selected: true, open: open, disabled: disabled, readonly: readonly, invalid: invalid }" @click="handleFocusIn()">
      {{ displaySelected || '&nbsp;' }}
    </div>
    <label>{{ label }}</label>
    <div class="items" ref="items" :class="{ selectHide: !open }">
      <div v-if="showSelectAll" @click="toggleSelectAll" class="dropdown-item select-all">
        <Checkbox :modelValue="isAllSelected" />
        <span class="item-text">{{ isAllSelected ? 'Deselect All' : 'Select All' }}</span>
      </div>
      <div v-for="(option, i) of options" :key="i" :style="{ display: option === '' ? 'none' : '' }" @click="selectOption(option)" :class="{ 'dropdown-item': true, hovering: focusedIndex === i }">
        <Checkbox :modelValue="isSelected(option)" />
        <span class="item-text">{{ option.value || option }}</span>
      </div>
      <span class="bottom-border"></span>
    </div>
  </div>
</template>

<script>
import Checkbox from './Checkbox.vue';

export default {
  name: 'MultiDropdown',
  components: {
    Checkbox,
  },
  emits: ['update:modelValue', 'change'],
  props: {
    options: {
      type: Array,
      required: true,
      validator: (value) => {
        return value.every((item) => typeof item === 'string' || (typeof item === 'object' && item !== null && 'value' in item));
      },
    },
    tabindex: {
      type: String,
      default: '0',
    },
    label: {
      type: String,
      default: '',
    },
    modelValue: {
      type: Array,
      required: true,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    showMissing: {
      type: Boolean,
      default: false,
    },
    showSelectAll: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    invalid: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      open: false,
      focusedIndex: null,
      pointerLocation: { x: null, y: null },
    };
  },
  computed: {
    displaySelected() {
      if (!this.modelValue || this.modelValue.length === 0) {
        return '';
      }

      const selectedTexts = this.modelValue.map((value) => {
        const option = this.options.find((opt) => (typeof opt === 'object' && opt.key === value) || opt === value);

        if (option) {
          return typeof option === 'object' ? option.value : option;
        } else if (this.showMissing) {
          return value;
        }
        return '';
      });

      return selectedTexts.join(', ');
    },
    isAllSelected() {
      return this.options.length > 0 && this.modelValue.length === this.options.length;
    },
  },
  methods: {
    isSelected(option) {
      const value = typeof option === 'object' ? option.key : option;
      return this.modelValue.includes(value);
    },
    toggleSelectAll() {
      if (this.disabled || this.readonly) return;

      const newModelValue = this.isAllSelected ? [] : this.options.map((option) => (typeof option === 'object' ? option.key : option));
      this.$emit('update:modelValue', newModelValue);
      this.$emit('change', newModelValue);
    },
    selectOption(option) {
      if (this.disabled || this.readonly) return;

      const value = typeof option === 'object' ? option.key : option;
      const newModelValue = [...this.modelValue];

      const index = newModelValue.indexOf(value);
      if (index === -1) {
        // Add item
        newModelValue.push(value);
      } else {
        // Remove item
        newModelValue.splice(index, 1);
      }

      this.$emit('update:modelValue', newModelValue);
      this.$emit('change', newModelValue);
    },
    handleFocusIn() {
      if (this.disabled || this.readonly) return;
      this.open = !this.open;
      if (this.open) {
        this.focusedIndex = null;
      }
    },
    calculatePosition() {
      const selectionEl = this.$refs.dropdownSelection;
      if (!selectionEl) return false;
      const itemsRect = selectionEl.getBoundingClientRect();
      return itemsRect.bottom > window.innerHeight - 200;
    },
    handleKeyDown(event) {
      if (this.disabled || this.readonly || !this.open) return;

      if (event.key !== 'Tab' && event.key.length === 1) {
        const letter = event.key.toLowerCase();
        const foundIndex = this.options.findIndex((option) => {
          const text = typeof option === 'object' ? option.value || '' : option;
          return String(text).toLowerCase().startsWith(letter);
        });

        if (foundIndex !== -1) {
          this.focusedIndex = foundIndex;
          this.$nextTick(() => {
            this.scrollToFocused();
          });
        }
      }
    },
    scrollToFocused() {
      const items = this.$refs.items;
      const hovering = items.querySelector('.hovering');
      if (hovering) {
        if (this.$refs.dropdownMenu.classList.contains('invert-to-top')) {
          items.scrollTop = hovering.offsetTop + items.offsetTop / 2;
        } else {
          items.scrollTop = hovering.offsetTop - items.offsetTop;
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.custom-select {
  position: relative;
  text-align: left;
  outline: none;
  max-width: 100%;
  min-width: 0;

  &.open {
    z-index: 1001;
  }

  .selected {
    background-color: white;
    border-radius: $borderRadius4;
    border: 1px solid $inputBorderColor;
    color: $textColor87;
    line-height: 1.5;
    cursor: pointer;
    user-select: none;
    padding: 0.85rem 2.5rem 0.7rem 0.7rem;
    height: 1.313rem;
    font-size: $fontSize14;
    transition: border-color 0.15s ease-in-out;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    width: 100%;
    box-sizing: border-box;
    height: 100%;

    &.disabled:not(.readonly) {
      color: $textColor12;
    }

    &.readonly {
      background: $textColor2;
    }

    &.open {
      border-radius: $borderRadius4 $borderRadius4 0px 0px;
      border: solid 2px $primaryActionColor;
      padding: calc(0.85rem - 1px) calc(2.5rem - 1px) calc(0.7rem - 1px) calc(0.7rem - 1px);
    }

    &.invalid {
      border: 2px solid $alertColor;
      padding: calc(0.85rem - 1px) calc(2.5rem - 1px) calc(0.7rem - 1px) calc(0.7rem - 1px);
    }

    &:after {
      position: absolute;
      content: '';
      top: 22px;
      right: 1rem;
      width: 0;
      height: 0;
      border: 5px solid transparent;
      border-color: $textColor87 transparent transparent transparent;
    }
  }

  .selected.open + label {
    color: $primaryActionColor;
  }

  .selected.invalid + label {
    color: $alertColor;
  }

  label {
    position: absolute;
    top: -0.32rem;
    left: 0.55rem;
    font-size: $fontSize10;
    color: $textColor60;
    background: white;
    padding: 0 0.3rem;
    border-radius: $borderRadius10;
    line-height: 1;
    user-select: none;
  }

  .items {
    color: $textColor87;
    overflow: hidden;
    position: absolute;
    background-color: white;
    left: 0;
    right: 0;
    z-index: 1001;
    border: 1px solid $inputBorderColor;
    border-top: none;
    border-bottom: none;
    max-height: 12rem;
    overflow-y: auto;

    .dropdown-item {
      display: flex;
      align-items: center;
      font-size: $fontSize14;
      color: $textColor87;
      padding: 0.6rem 0.7rem;
      cursor: pointer;
      user-select: none;

      &.select-all {
        color: $textColor60;
      }

      &:hover,
      &.hovering {
        background-color: $arrowGrayColor;
      }

      .item-text {
        margin-left: 0.5rem;
      }
    }

    .bottom-border {
      height: 1px;
      background-color: $inputBorderColor;
      position: sticky;
      display: block;
      bottom: 0;
      left: 0;
      right: 0;
    }
  }

  &.invert-to-top {
    .items {
      bottom: 100%;
      top: auto;
      border-top: 1px solid $inputBorderColor;
      border-bottom: none;
    }

    .selected.open {
      border-radius: 0px 0px $borderRadius4 $borderRadius4;
    }
    label {
      z-index: 2;
    }
  }
}

.selectHide {
  display: none;
}
</style>
